/**
 * QuestionCard Component - SSR-Safe Version
 *
 * Versão simplificada do QuestionCard original, otimizada para SSR.
 * Características:
 * - Sem dependências de window/localStorage/document fora de useEffect
 * - Guards obrigatórios antes de acessar APIs do navegador
 * - Renderização básica funcional no servidor
 * - Hydratação suave no cliente
 */

import { useState, useEffect, useRef } from 'react'

// ========================================
// TYPES
// ========================================

interface NormalizedAnswer {
  id: string
  text: string
  order_index: number
  explanation?: string | null
}

interface Question {
  id: string
  category?: {
    name: string
    slug: string
    icon?: string
    color?: string
  }
  text: string
  image_url?: string
  answers: NormalizedAnswer[]
  correct_answer_index?: number
  difficulty: string
}

interface AnswerResult {
  questionId: string
  isCorrect: boolean
  correctIndex: number
  correctAnswerId?: string | null
  xpEarned: number
  coinsEarned: number
  selectedAnswerExplanation?: string | null
  correctAnswerExplanation?: string | null
}

interface QuestionCardProps {
  question: Question
  result?: AnswerResult
  onAnswer: (answerId: string, timeSpentMs?: number) => void
  isActive: boolean
  questionNumber?: number
  totalQuestions?: number
}

// ========================================
// COMPONENT
// ========================================

export default function QuestionCard({
  question,
  result,
  onAnswer,
  isActive: _isActive,
  questionNumber,
  totalQuestions,
}: QuestionCardProps) {
  // Unused props marked with underscore
  void _isActive

  // ========================================
  // STATE
  // ========================================

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [showExplanation, setShowExplanation] = useState(false)

  // SSR-SAFE: useRef é seguro no servidor (retorna null)
  const startTimeRef = useRef<number>(0)

  // ========================================
  // EFFECTS
  // ========================================

  // SSR-SAFE: useEffect NÃO executa no servidor
  useEffect(() => {
    // Guard: apenas no cliente
    if (typeof window !== 'undefined') {
      startTimeRef.current = Date.now()
    }
  }, [])

  // Reset state quando questão mudar
  useEffect(() => {
    setSelectedAnswer(null)
    setShowResult(false)
    setImageError(false)
    setShowExplanation(false)

    // SSR-SAFE: Guard antes de acessar Date
    if (typeof window !== 'undefined') {
      startTimeRef.current = Date.now()
    }
  }, [question.id])

  // Mostrar resultado quando receber resposta do backend
  useEffect(() => {
    if (result) {
      setShowResult(true)

      // Auto-hide result e mostrar explicação se errou
      const timer = setTimeout(() => {
        setShowResult(false)
        if (!result.isCorrect) {
          setShowExplanation(true)
        }
      }, result.isCorrect ? 1200 : 800)

      return () => clearTimeout(timer)
    }
  }, [result])

  // ========================================
  // HANDLERS
  // ========================================

  const handleAnswer = (answerIndex: number) => {
    if (result || selectedAnswer !== null) return

    setSelectedAnswer(answerIndex)
    const clickedAnswer = question.answers[answerIndex]

    // Calcular tempo gasto (SSR-SAFE)
    let timeSpentMs: number | undefined
    if (typeof window !== 'undefined' && startTimeRef.current > 0) {
      timeSpentMs = Date.now() - startTimeRef.current
    }

    // Enviar resposta usando UUID (fonte da verdade)
    onAnswer(clickedAnswer.id, timeSpentMs)
  }

  // ========================================
  // DERIVED STATE
  // ========================================

  // Ordenar respostas por order_index
  const sortedAnswers = [...question.answers].sort((a, b) => a.order_index - b.order_index)

  // Encontrar índice da resposta correta
  const correctIndex = result?.correctAnswerId
    ? sortedAnswers.findIndex(ans => ans.id === result.correctAnswerId)
    : question.correct_answer_index ?? 0

  // Letras das alternativas
  const letters = ['A', 'B', 'C', 'D', 'E']

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="relative h-full w-full bg-black overflow-hidden">
      {/* Header - Question Counter */}
      <div className="absolute top-4 left-0 right-0 z-30">
        <div className="flex justify-center">
          <div className="px-4 py-2 rounded-full bg-white/5 border border-white/10">
            <span className="text-xs text-white/60">
              Questão {questionNumber ?? 1} de {totalQuestions ?? 1}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="absolute inset-0 pt-16 pb-20 px-6 flex flex-col">
        {/* Categoria Badge */}
        {question.category && (
          <div className="mb-3 flex-shrink-0">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/10">
              {question.category.icon && <span className="mr-1.5">{question.category.icon}</span>}
              {question.category.name}
            </div>
          </div>
        )}

        {/* Imagem (se houver) */}
        {question.image_url && !imageError && (
          <div className="mb-4 flex-shrink-0">
            <div className="w-full aspect-video rounded-xl bg-white border border-white/20 relative overflow-hidden shadow-lg">
              <img
                src={question.image_url}
                alt="Imagem da questão"
                onError={() => setImageError(true)}
                className="absolute inset-0 w-full h-full object-contain p-6"
              />
            </div>
          </div>
        )}

        {/* Texto da Questão */}
        <div className="mb-4 flex-shrink-0">
          <h2 className="text-base font-medium text-white leading-relaxed">
            {question.text}
          </h2>
        </div>

        {/* Alternativas */}
        <div className="flex-1 overflow-y-auto space-y-2.5 min-h-0">
          {sortedAnswers.map((answer, answerIndex) => {
            const isSelected = selectedAnswer === answerIndex
            const isTheCorrectAnswer = answerIndex === correctIndex

            // Determinar estado visual
            const isCorrect = result && (
              result.isCorrect === true
                ? isSelected
                : isTheCorrectAnswer
            )
            const isWrong = result && isSelected && !result.isCorrect && !isTheCorrectAnswer

            return (
              <button
                key={answer.id || answerIndex}
                onClick={() => handleAnswer(answerIndex)}
                disabled={!!result}
                className={`
                  group relative w-full p-4 rounded-xl text-left transition-all
                  flex items-center gap-3
                  ${!result && !isSelected ? 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-green-500/50' : ''}
                  ${!result && isSelected ? 'bg-cyan-500/20 border border-cyan-400' : ''}
                  ${isCorrect ? 'bg-green-500/15 border-2 border-green-500 shadow-lg' : ''}
                  ${isWrong ? 'bg-red-500/15 border-2 border-red-500 shadow-lg' : ''}
                  ${result && !isCorrect && !isWrong ? 'opacity-40 border border-transparent' : ''}
                `}
              >
                {/* Barra de acento lateral */}
                <div className={`
                  absolute left-0 top-0 bottom-0 w-1.5 rounded-l-xl transition-colors
                  ${!result && !isSelected ? 'bg-transparent group-hover:bg-green-500' : ''}
                  ${isCorrect ? 'bg-green-500' : ''}
                  ${isWrong ? 'bg-red-500' : ''}
                  ${!result && isSelected ? 'bg-cyan-400' : ''}
                `} />

                {/* Badge da letra */}
                <span className={`
                  w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0
                  ${isCorrect ? 'bg-green-500 text-black' : ''}
                  ${isWrong ? 'bg-red-500 text-white' : ''}
                  ${isSelected && !result ? 'bg-cyan-500 text-white' : ''}
                  ${!isSelected && !result ? 'bg-white/10 text-white/70 group-hover:bg-green-500 group-hover:text-black' : ''}
                `}>
                  {isCorrect ? '✓' : isWrong ? '✗' : letters[answerIndex]}
                </span>

                {/* Texto da resposta */}
                <span className={`
                  flex-1 text-sm leading-relaxed
                  ${isCorrect ? 'text-green-500 font-medium' : ''}
                  ${isWrong ? 'text-red-500/90' : ''}
                  ${!isCorrect && !isWrong ? 'text-white/90' : ''}
                `}>
                  {answer.text}
                </span>
              </button>
            )
          })}
        </div>

        {/* Botão para ver explicação (quando acertou) */}
        {result && result.isCorrect && !showExplanation && (
          <button
            onClick={() => setShowExplanation(true)}
            className="mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/20 border border-amber-400/50 rounded-full hover:bg-amber-500/30 transition-all"
          >
            <span className="text-amber-400">💡</span>
            <span className="text-sm font-medium text-amber-200">Ver explicação</span>
          </button>
        )}
      </div>

      {/* Flash de feedback visual */}
      {showResult && result && (
        <div
          className="absolute inset-0 pointer-events-none z-40 animate-pulse"
          style={{
            backgroundColor: result.isCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            animation: 'pulse 0.6s ease-out',
          }}
        />
      )}

      {/* Celebração de XP (quando acertar) */}
      {showResult && result && result.isCorrect && (
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="px-8 py-4 rounded-2xl bg-gradient-to-r from-green-400 to-cyan-400 shadow-2xl">
              <div className="flex items-center gap-2 text-black">
                <span className="text-3xl font-black">+{result.xpEarned}</span>
                <span className="text-2xl font-black">XP</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Painel de Explicação */}
      {showExplanation && result && (
        <div className="absolute bottom-0 left-0 right-0 z-40 max-h-[70%] overflow-hidden bg-gradient-to-t from-black via-black/95 to-black/90 backdrop-blur-xl border-t border-white/10 rounded-t-3xl">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <button
              onClick={() => setShowExplanation(false)}
              className="w-12 h-1.5 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-amber-400">💡</span>
              </div>
              <span className="text-sm font-bold text-white">Entenda a questão</span>
            </div>
            <button
              onClick={() => setShowExplanation(false)}
              className="text-xs text-white/50 hover:text-white/70 transition-colors"
            >
              Fechar
            </button>
          </div>

          {/* Content */}
          <div className="px-5 pb-6 overflow-y-auto max-h-[50vh]">
            <div className="space-y-4">
              {/* Resposta errada (se aplicável) */}
              {!result.isCorrect && selectedAnswer !== null && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-red-500">✗</span>
                    <span className="text-xs font-semibold text-red-500">
                      Sua resposta ({letters[selectedAnswer]})
                    </span>
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {result.selectedAnswerExplanation ||
                     sortedAnswers[selectedAnswer]?.explanation ||
                     'Esta alternativa está incorreta.'}
                  </p>
                </div>
              )}

              {/* Resposta correta */}
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-xs font-semibold text-green-500">
                    {result.isCorrect ? 'Por que está correta' : `Resposta correta (${letters[correctIndex]})`}
                  </span>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  {result.correctAnswerExplanation ||
                   sortedAnswers[correctIndex]?.explanation ||
                   sortedAnswers[correctIndex]?.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barra de resultado no topo */}
      {result && (
        <div
          className="absolute top-4 left-0 right-0 h-1.5"
          style={{
            backgroundColor: result.isCorrect ? '#22c55e' : '#ef4444',
            boxShadow: result.isCorrect
              ? '0 0 10px rgba(34, 197, 94, 0.5)'
              : '0 0 10px rgba(239, 68, 68, 0.5)',
          }}
        />
      )}
    </div>
  )
}
